import { Fragment, useEffect, useRef, useState } from 'react';

import { getLinkAriaLabel } from '@/utils/urlUtils';

import { useHandleUrlTrigger } from '@/components/Cms/NavQuickLinks/components/NavQuickLinkItem/helpers';
import type { ButtonVariant } from '@/components/LegacyButton';
import { VideoPlayer } from '@/components/Video';
import type { Maybe } from '@/lib/types';
import { useRegionProvider } from '@/providers/RegionProvider';
import type { ButtonColorVariant } from '@/theme/types/components';

import type { CardProps } from '../types';

import * as Gs from '../Card.styles';

export const externalLinkReg = /^(http|https):\/\//;

interface CtaSetting {
  colorVariant?: ButtonColorVariant;
  isUpperCase: boolean;
  lower?: boolean;
  suffix: boolean;
  underline: boolean;
  variant: ButtonVariant;
}
const CtaSettingMap: Record<string, CtaSetting> = {
  button: {
    underline: false,
    variant: 'filled',
    suffix: false,
    isUpperCase: true,
  },
  buttonDefault: {
    underline: false,
    variant: 'filled',
    isUpperCase: true,
    suffix: false,
  },
  buttonUnderline: {
    underline: true,
    variant: 'link',
    colorVariant: 'white',
    isUpperCase: true,
    suffix: false,
  },
  buttonFilledBlack: {
    underline: false,
    variant: 'filled',
    colorVariant: 'black',
    isUpperCase: true,
    suffix: false,
  },
  buttonOutlinePrimary: {
    underline: false,
    variant: 'outlined',
    colorVariant: 'primary',
    isUpperCase: true,
    suffix: false,
  },
  buttonOutlineBlack: {
    underline: false,
    variant: 'outlined',
    colorVariant: 'black',
    isUpperCase: true,
    suffix: false,
  },
  buttonOutlineBlue: {
    underline: false,
    variant: 'outlined',
    colorVariant: 'blue',
    isUpperCase: true,
    suffix: false,
  },
  buttonOutlineWhite: {
    underline: false,
    variant: 'outlined',
    colorVariant: 'white',
    isUpperCase: true,
    suffix: false,
  },
  linkWhite: {
    colorVariant: 'white',
    variant: 'link',
    underline: true,
    isUpperCase: false,
    suffix: true,
    lower: false,
  },
  linkBlack: {
    colorVariant: 'black',
    variant: 'link',
    underline: true,
    isUpperCase: false,
    suffix: true,
    lower: false,
  },
  linkDefault: {
    colorVariant: 'primary',
    variant: 'link',
    underline: true,
    isUpperCase: false,
    suffix: true,
    lower: false,
  },
  default: {
    colorVariant: 'white',
    variant: 'link',
    underline: false,
    isUpperCase: false,
    suffix: true,
    lower: false,
  },
  primaryBtn: {
    colorVariant: 'primary',
    variant: 'filled',
    underline: false,
    isUpperCase: true,
    suffix: true,
    lower: false,
  },
  arrowOutlineWhite: {
    colorVariant: 'primary',
    variant: 'outlined',
    underline: false,
    isUpperCase: true,
    suffix: false,
    lower: false,
  },
};

//TODO refactor this
export const Cta = ({
  ariaLabel,
  fluid = false,
  onClickCta,
  openUrlInNewTab = false,
  url,
  urlEventType = 'Default',
  urlText,
  urlTrigger,
  urlType,
}: Pick<
  CardProps,
  | 'openUrlInNewTab'
  | 'onClickCta'
  | 'url'
  | 'urlText'
  | 'urlType'
  | 'urlTrigger'
> & {
  ariaLabel?: string;
  fluid?: Maybe<boolean>;
  urlEventType?: string;
}) => {
  const { handleUrlTrigger } = useHandleUrlTrigger(urlTrigger);
  const { country, locale } = useRegionProvider();

  const modalTargetElement = useRef<HTMLElement | null>(null);
  useEffect(() => {
    modalTargetElement.current = document.getElementById('modal-root');
  });

  const [openCTAVideoPopup, setOpenCTAVideoPopup] = useState<boolean>(false);

  if (!urlText) {
    return <Fragment data-test-id='CtaFragment' />;
  }

  const { colorVariant, isUpperCase, lower, suffix, underline, variant } =
    CtaSettingMap[urlType ?? ''] ?? CtaSettingMap['primaryBtn'];

  const processedUrl = url
    ?.replace(/\{locale\}/g, locale.slug)
    .replace(/\{country\}/g, country.slug);
  const finalUrl = processedUrl ?? '';
  const isExternalLink = externalLinkReg.test(finalUrl);

  const onClickHandle = (e: { preventDefault: () => void }) => {
    if (urlEventType === 'OpenVideoPopup' && modalTargetElement.current) {
      setOpenCTAVideoPopup(true);
    }
    if (urlEventType === 'OpenSelectorPopup') {
      document.body.dispatchEvent(new CustomEvent('openSelectorModal'));
      e.preventDefault();
    }
    if (urlTrigger) {
      e.preventDefault();
      handleUrlTrigger();
    } else if (onClickCta) {
      onClickCta();
    }
  };

  const onClose = () => {
    setOpenCTAVideoPopup(false);
  };

  return openCTAVideoPopup ? (
    <VideoPlayer
      data-testid='videoPopupModal'
      mediaUrl={url}
      onClose={onClose}
    />
  ) : (
    <Gs.Button
      $lower={lower}
      $rowReverse={urlType === 'arrowOutlineWhite'}
      $underline={underline}
      ariaLabel={ariaLabel || getLinkAriaLabel(finalUrl)}
      colorVariant={colorVariant}
      data-test-id='CtaButton'
      href={isExternalLink && urlEventType === 'Default' ? finalUrl : undefined}
      isFluid={!!fluid}
      isUpperCase={isUpperCase}
      label={urlText}
      linkProps={
        !isExternalLink && urlEventType === 'Default'
          ? { href: finalUrl }
          : undefined
      }
      onClick={onClickHandle}
      openUrlInNewTab={openUrlInNewTab ?? false}
      variant={variant}
    >
      {suffix && <Gs.Chevron data-test-id='CtaChevron' height={24} />}
      {urlType === 'arrowOutlineWhite' && (
        <Gs.LongArrow data-test-id='CtaLongArrow' height={24} />
      )}
    </Gs.Button>
  );
};
